import React, { useEffect, useState } from 'react';
import ToggleButtons from '../../components/Shared/ToggleButtons/ToggleButtons';
import AddButton from '../../components/Shared/AddButton/AddButton';
import ExportButton from '../../components/Shared/ExportButton/ExportButton';
import SearchBar from '../../components/Shared/SearchBar/SearchBar';
import PaginationInfo from '../../components/Shared/Pagination/PaginationInfo';
import Pagination3 from '../../components/Shared/Pagination/Pagination3';

import { Link, useNavigate } from 'react-router-dom';

import Table from '../../components/Shared/Table/BatchesTable';
import styles from './Batches.module.css';
import { BatchesService } from '../../services/BatchesService';
import { BatchDTO, BatchResponseDTO } from '../../dto/BatchesDTO';
import { SelectChangeEvent } from '@mui/material';
import { showErrorToast, showSuccessToast } from '../../components/Shared/Toastify/toastHelper';
import ArchiveModal from '../../components/Shared/ArchiveModal/ArchiveModal';

const Batches: React.FC = () => {
    const columns = [
        { title: 'Batch Title' },
        { title: 'Type' },
        { title: 'Publisher' },
        { title: 'Book Title' },
        { title: 'Grades' },
        { title: 'Institution' },
        { title: 'All Codes' },
        { title: 'Expires At' },
        { title: 'Created Date' },
    ];

    const navigate = useNavigate();
    const [activeButton, setActiveButton] = useState(0);
    const [batches,setBatches]=useState<BatchDTO[]>([]);
    const [total,setTotal]=useState(0);
    
    
    const [showModal,setShowModal]=useState(false)
    const [selectedBatch,setSelectedBatch]=useState<BatchDTO|null>(null)
    const [mode, setMode] = useState<'archive' | 'delete'|'publish'>('archive'); 
    

    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm,setSearchTerm] = useState<string>("");

      const [sortColumn, setSortColumn] = useState<string | null>(null);
      const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [loading,setLoading]=useState(false);

    const handleConfirmAction = async (): Promise<void> => {
        if (selectedBatch) {  
                try {
                    if (mode === 'archive') {
                        const message=await BatchesService.toggleArchive( selectedBatch._id);
                        showSuccessToast(message);
                    } else if (mode === 'delete') {
                        const message=await BatchesService.deleteBatch(selectedBatch._id);
                        showSuccessToast(message);
                    }else if(mode ==='publish'){
                        const message  =await BatchesService.toggleActivate( selectedBatch._id);
                        showSuccessToast(message);
                    }
                    setSelectedBatch(null);
                    await fetchBatches();
                } catch (error:any) {
                    const errorMessage=error.message
                    if (mode === 'archive') {
                       showErrorToast('Error toggling archive batch: '+errorMessage)
                    } else if (mode === 'delete') {
                        showErrorToast('Error deleting batch: '+errorMessage)

                    }else if(mode ==='publish'){
                        showErrorToast('Error toggling publish batch: '+errorMessage)

                    }
                }
            
        }
        setShowModal(false);
    };

    const handleCancelAction = (): void => {
        setSelectedBatch(null);
        setShowModal(false);
    };

    const handleArchiveClick = (id: BatchDTO): void => {
        setSelectedBatch(id);
        setMode('archive');
        setShowModal(true);
    };

    const handlePublishClick = (id: BatchDTO): void => {
        setSelectedBatch(id);
        setMode('publish');
        setShowModal(true);
    };

    const handleDeleteClick = (id:BatchDTO): void => {
        setSelectedBatch(id);
        setMode('delete'); 
        setShowModal(true);
    };

    const handleSearchChange = (value: string) => {
        setLoading(true)
        setCurrentPage(1)
        setSearchTerm(value); 
    };

    const handlePageSizeChange = (event: SelectChangeEvent<unknown>) => {
        setLoading(true)
        setPageSize(Number(event.target.value));
        setCurrentPage(1)
    };

    const handlePageChange = (page: number) => {
        setLoading(true)   
        setCurrentPage(page);
    };

    const handleEditClick = (id: string) => {
        navigate(`/batches/edit/${id}`);
    };

    const handleViewClick = (id: string) => {
        navigate(`/batches/view/${id}`);
    };

    
    
    const fetchBatches = async () => {
        try {
            setLoading(true)
        
            const active=activeButton===0?true:activeButton===1?false:false
            const archived=activeButton===2?true:false
            const batchData:BatchResponseDTO = await BatchesService.getBatches(currentPage,pageSize,active,archived,searchTerm,sortColumn,sortDirection);
            if(batchData && batchData.batches && Array.isArray(batchData.batches)){
                setTotal(batchData.total)
                setBatches(batchData.batches);
            }
            
        } catch (error:any) {
            showErrorToast('Error while fetching batches: '+error.message)
            
        }finally{
            setLoading(false)
        }
    };
    //fetch data
    useEffect(() => {  
        fetchBatches();
    }, [currentPage,pageSize,activeButton,searchTerm,sortColumn,sortDirection]);

    const label = [
        { label_name: "Active", index: 0 },
        { label_name: "Deactivated", index: 1 },
        { label_name: "Archived", index: 2 },
    ];

    const formatDate = (oldDate:string): string => {
        if(!oldDate){
            return 'null'
        }
        const date = new Date(oldDate);
        const formattedDate = date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
        return formattedDate;
    }

    const tableData = batches?.map((batch) => {
         return {
            '_id': batch._id,
            'Batch Title': batch.title || null,
            'Type': batch.licenseType || null,
            'Publisher': batch.book?.publisher?.fullname || null,
            'Grades':batch.book.type==='university book'?"Book": batch.book?.grades?.map(grade => batch.book?.language=="English"? grade.engName:batch.book?.language=="French"?grade.frName:grade.arName).join(', '),
            'Book Title': batch.book?.bookTitle || null,
            'Institution': batch.school?.name || null,
            'All Codes': `${batch.activatedAccessCodes||0}/${batch.totalAccessCodes || 0}`,
            'Created Date': formatDate(batch.dateCreated),
            'Expires At': formatDate(batch.expirationDate)||null,
            'Active':batch.active,
            'Archived':batch.archived,
            'Language': batch.book?.language
        };
    });

    const handleSetActiveButton=(value:number)=>{
        setActiveButton(value)
        setCurrentPage(1)
    }
    const columnKeyMapping: { [key: string]: string } = {
        'Batch Title': 'title',
        'Type': 'licenseType',
        'Publisher': 'publisher',
        "Grades":'grades',
        'Book Title': 'bookTitle',
        'Institution': 'schoolName',
        'All Codes': 'totalAccessCodes',
        'Used Codes': 'activatedAccessCodes',
        'Created Date': 'dateCreated',
        'Expires At': 'expirationDate',
    };
    
    const handleSort = (column: string) => {
        const backendKey = columnKeyMapping[column];
        if (!backendKey) return;
        if(loading) return;
        setLoading(true)
        if (sortColumn === backendKey) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(backendKey);
            setSortDirection('asc');
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.content}>
            <ToggleButtons labels={label} activeButton={activeButton} setActiveButton={handleSetActiveButton} />
            <div className='flex'>
                <Link to="/batches/add" className={styles.link}>
                    <AddButton label="ADD BATCH" />
                </Link>
                <ExportButton label="Export" exportType='batches' />
            </div>
            </div>
            <div className={styles.content2}>
                <SearchBar searchTerm={searchTerm} onSearch={handleSearchChange}/>
                <PaginationInfo 
                  totalItems={total} 
                  pageSize={pageSize}
                  currentPage={currentPage}
                  handlePageSizeChange={handlePageSizeChange}
                />
            </div>
            <Table
                columns={columns}
                data={tableData}
                onEdit={handleEditClick}
                loading={loading}
                handleActivate={handlePublishClick} 
                handleArchive={handleArchiveClick}
                handleDelete={handleDeleteClick}
                onView={handleViewClick}
                handleSort={handleSort}
            />
            <div className={styles.content3}>
                <Pagination3 totalPages={Math.ceil(total / pageSize)} currentPage={currentPage} onPageChange={handlePageChange} />
            </div>
            <ArchiveModal
                setIsOpen={setShowModal}
                isOpen={showModal}
                onConfirm={handleConfirmAction}
                onCancel={handleCancelAction}
                header={`Sure you want to ${mode==='archive' ? selectedBatch?.Archived===false?'archive':"Unarchive" : mode==='delete'? 'delete':mode==='publish'? selectedBatch?.Active===true?'Deactivate':"Activate":''} this batch?`}
                parag={`Sure you want to ${mode==='archive' ? selectedBatch?.Archived===false?'archive':"Unarchive" : mode==='delete'? 'delete':mode==='publish'? selectedBatch?.Active===true?'Deactivate':"Activate":''} this batch?`}
                mode={mode}
                buttonText={mode==='archive' ? selectedBatch?.Archived===false?'Yes, archive':"Yes, Unarchive" : mode==='delete'? 'Yes, delete':mode==='publish'? selectedBatch?.Active===true?'Yes, Deactivate':"Yes, Activate":''}
            />
        </div>
    );
}

export default Batches;
